import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';

import { objectChanged } from '../state/core.actions';
import { feedUnloaded } from '../state/feed.actions';

/**
 * Per-widget view state that is **never persisted**:
 *
 * - `is_visible` - viewport bookkeeping. The card sets it from its intersection
 *   observer and reads it back before reflowing Highcharts; the dashboard forces
 *   it true for the current page when leaving Presentation Mode.
 * - `chart_options` - the rendered Highcharts config, base64-decoded by the card
 *   and shown in the widget-settings "debug chart JSON" tab.
 *
 * Deliberately **not** in the query cache: a refetch would revert them, and the
 * cache would then misreport what the server holds.
 *
 * A root service rather than a component signal because both fields have more than
 * one writer or reader across components.
 */
@Injectable({ providedIn: 'root' })
export class WidgetViewStateService {
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);

  private readonly visibility = signal<ReadonlyMap<string, boolean>>(new Map());
  private readonly chartOptions = signal<ReadonlyMap<string, string>>(
    new Map(),
  );

  constructor() {
    // * both fields are per-view, so they are dropped when the view changes
    this.actions$
      .pipe(
        ofType(objectChanged, feedUnloaded),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.visibility.set(new Map());
        this.chartOptions.set(new Map());
      });
  }

  isVisible(widgetId: string): boolean {
    return this.visibility().get(widgetId) ?? false;
  }

  setVisible(widgetId: string, isVisible: boolean): void {
    this.visibility.set(new Map(this.visibility()).set(widgetId, isVisible));
  }

  getChartOptions(widgetId: string): string | undefined {
    return this.chartOptions().get(widgetId);
  }

  setChartOptions(widgetId: string, chartOptions: string): void {
    this.chartOptions.set(
      new Map(this.chartOptions()).set(widgetId, chartOptions),
    );
  }
}
